import { DependencyList } from 'react';
import { hasModifierKey } from '@platform/shortcuts/utils/keyboard-platform';
import {
  type HotkeyCallback,
  type Options,
  useHotkeys,
} from 'react-hotkeys-hook';
import { KeyboardScope, logKey } from '@/platform/shortcuts/operator-shortcuts';
import { Platform } from 'react-native';
import { TrackingProperties } from '@/utils/analytics/base-reporter';
import { useLayoutContext } from '@/contexts/layout-context';

/**
 * Hook for registering keyboard shortcuts on web.
 * Accepts any hotkey string (from AllowedShortcuts, SalesPortalShortcuts, or raw strings).
 * Each experience can define its own shortcuts enum while using this shared hook.
 *
 * @param hotKey - Hotkey string(s) to register.
 * @param callback - Handler invoked when the hotkey fires.
 * @param dependencies - Dependency list (or options object) for the underlying hook.
 * @param trackingData - Analytics properties logged alongside the shortcut.
 * @param enabled - Whether the shortcut is currently active.
 * @param scope - Keyboard scope(s) the shortcut belongs to.
 * @param enableOnFormTagsOverride - When provided, overrides the default
 *   `enableOnFormTags` behaviour. By default only modifier shortcuts (e.g.
 *   Alt+X) fire on form elements. Pass `true` to force a single-key shortcut
 *   to fire on form elements (e.g. arrow/enter keys in an open popover menu).
 */
export const useKeyboardShortcuts = (
  hotKey: string | string[],
  callback: HotkeyCallback,
  dependencies: Options | DependencyList,
  trackingData: TrackingProperties = {},
  enabled = true,
  scope?: string | string[],
  enableOnFormTagsOverride?: boolean,
) => {
  const { isSmallLayout } = useLayoutContext();
  const isKeyboardShortcutsAllowed = Platform.OS === 'web' && !isSmallLayout;

  // only enable on form tags for modifier shortcuts (Alt+X, Shift+X)
  // single-key shortcuts (C, E, /) should not fire when typing in inputs
  const enableOnFormTags = enableOnFormTagsOverride ?? hasModifierKey(hotKey);

  useHotkeys(
    hotKey,
    (keyboardEvent, hotkeysEvent) => {
      logKey(hotKey, trackingData);
      callback(keyboardEvent, hotkeysEvent);
    },
    {
      enabled: isKeyboardShortcutsAllowed && enabled,
      enableOnContentEditable: enableOnFormTags,
      enableOnFormTags,
      keydown: true,
      preventDefault: true,
      scopes: scope || KeyboardScope.SCOPED,
    },
    dependencies,
  );
};
